import { configureStore } from "@reduxjs/toolkit";
import {
  baseApi,
  connectBaseApi,
  networkReducer,
  createRtkqLoadingMiddleware,
  createGlobalErrorMiddleware,
} from "@racoon/api";
import { companyReducer } from "./company/company.slice";
import { statsReducer } from "./stats/stats.slice";
import { uiReducer } from "./ui/ui.slice";

const rtkqLoadingMiddleware = createRtkqLoadingMiddleware([baseApi, connectBaseApi]);
const globalErrorMiddleware = createGlobalErrorMiddleware(
  [baseApi, connectBaseApi],
  [
    "completeDriverOnShift",
    "heartbeat",
    "statusPoll",
    "getScaleRecordByRoute",
    "getPseudoRouteConstraints",
    // The schedule editor shows these errors in its form.
    "createContainerGroupSchedule",
    "updateContainerGroupSchedule",
    "previewSchedule",
    // One foreign or missing id denies the whole list; readers fall back to "unknown".
    "listContainerGroupSchedules",
    // ponytail: hides every depart_info failure, not just the 500 after a route
    // without unloading; assign-route falls back to the depot. Drop this entry once
    // go-backend serves depart info (INTEG-307).
    "getDepartInfo",
  ],
);

export const store = configureStore({
  reducer: {
    company: companyReducer,
    stats: statsReducer,
    ui: uiReducer,
    [baseApi.reducerPath]: baseApi.reducer,
    [connectBaseApi.reducerPath]: connectBaseApi.reducer,
    network: networkReducer,
  },
  // Dev-only checks: they walk every action and the whole state, which here holds tens of MB of
  // cached protobuf messages (bigint everywhere, so serializable checks can't pass anyway).
  // That froze every dispatch for ~1 s; RTK Query keeps its own cache immutable.
  middleware: (gDM) =>
    gDM({
      serializableCheck: false,
      immutableCheck: { ignoredPaths: [baseApi.reducerPath, connectBaseApi.reducerPath] },
    }).concat(
      baseApi.middleware,
      connectBaseApi.middleware,
      rtkqLoadingMiddleware,
      globalErrorMiddleware,
    ),
});

export type RootState = ReturnType<typeof store.getState>;
